/**
 * The model editor (models.md §12; the Filter editor's shape): `EditorFrame` with the **Enabled** switch in
 * the header, then
 *   1. Overview — recommendation, description, the accuracy and speed bars, facts (accuracy, speed,
 *      languages with the examples dialog, size or where it runs, memory), the measured-on line and the
 *      caveats in full; for an on-device model the download block ("On this device"), for Jev today's
 *      usage against the budget;
 *   2. Runs when — trigger rows;
 *   3. Only if — the shared condition groups with the run-rule fields, and Test (`models.test`);
 *   4. Actions on scoring — the scoring rules that test this model, Add action, Reset ladder;
 *   5. Options — "Reads from a topic", Jev's text length and daily budget (quick controls' Budget, moved
 *      here unchanged in what they do), the lowest score that shows a topic;
 *   6. Order — the first model's scores are the ones shown.
 * Save sends only what changed (`models.update`); the engine's refusals are shown at the field.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  toDigesterError,
  type AccountResponse,
  type DigesterError,
  type DigesterErrorShape,
  type Model,
  type ModelCatalogEntry,
  type ModelId,
  type ModelInstallState,
  type ModelRunTestResult,
  type PipelineStatus,
  type ScoringRule,
  type TopicField,
} from '@digester/core';
import { useAccountState } from '../../account/shared.js';
import { openDialog } from '../../app/dialogs.js';
import { reportError } from '../../app/errors.js';
import { useEntitlement, useFolders } from '../../app/hooks.js';
import { scoringPauseText } from '../../app/scoring-pause.js';
import { useServices } from '../../app/services.js';
import { showToast } from '../../app/Toast.js';
import { useEngine, useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { Icon } from '../../icons/Icon.js';
import { formatExact } from '../../ui/format.js';
import { Field, Section, Select } from '../../ui/form.js';
import { Tool } from '../../ui/Tool.js';
import { ruleSummary } from '../rules/model.js';
import { ConditionGroups } from '../rules/RuleEditor.js';
import { EditorFrame, SaveIslandTools, useDraft } from '../shell/index.js';
import { accuracyValue, aucText, megabytes, modelStatus, speedText, speedValue } from './catalog.js';
import { isOnDevice } from './data.js';
import { InstallBlock } from './InstallBlock.js';
import { Meter } from './Meter.js';
import { actionsOfModel, patchOf, runOf, sortIssues, toModelDraft, TRIGGER_LABELS, type ModelDraft, type TriggerDraft } from './run.js';

/** "Text sent per message" steps of quick controls; 'all' = the ceiling `scoring.body_max_chars` (6,000). */
export const BODY_STEPS = ['500', '1000', '3000', 'all'] as const;
type BodyStep = (typeof BODY_STEPS)[number];
const BODY_ALL = 6000;
const bodyStepOf = (chars: number): BodyStep => (chars <= 500 ? '500' : chars <= 1000 ? '1000' : chars <= 3000 ? '3000' : 'all');

const FIELD_LABEL: Readonly<Record<TopicField, string>> = { name: 'Name', description: 'Description', phrases: 'Paraphrases' };

function useScoredToday(enabled: boolean): number | null {
  const { api } = useServices();
  const account = useAccountState();
  const server = useQuery<AccountResponse, DigesterError>({ queryKey: ['account', 'server'], queryFn: () => api.call<AccountResponse>('account'), enabled: account.signedIn && enabled, refetchInterval: 60_000 });
  return server.data?.usage.scoredToday ?? null;
}

export interface ModelEditorProps {
  model: Model;
  entry: ModelCatalogEntry;
  models: readonly Model[];
  rules: readonly ScoringRule[];
  install: ModelInstallState | undefined;
  unavailable: string | null;
}

export function ModelEditor({ model, entry, models, rules, install, unavailable }: ModelEditorProps) {
  const engine = useEngine();
  const navigate = useNavigate();
  const folders = useFolders();
  const entitlement = useEntitlement();
  const pipeline = useEngineQuery(qk.pipeline(), (e) => e.pipeline.status()).data;
  const usage = useEngineQuery(qk.plan('usage'), (e) => e.plan.usage()).data;
  const [issues, setIssues] = useState<ReturnType<typeof sortIssues> | null>(null);
  const [test, setTest] = useState<ModelRunTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const draft = useDraft<ModelDraft>({
    initial: toModelDraft(model),
    onSave: async (v) => {
      setIssues(null);
      const patch = patchOf(model, v);
      if (Object.keys(patch).length === 0) return;
      try {
        await engine.models.update(model.id, patch);
      } catch (error) {
        const e = toDigesterError(error);
        if (e.code === 'invalid_input' && e.details?.issues?.length) setIssues(sortIssues(e.details.issues, v.groups.length));
        throw e;
      }
    },
  });
  const v = draft.value;
  const onDevice = isOnDevice(entry) ? entry : null;
  const status = modelStatus({ ...model, enabled: v.enabled }, entry, install, unavailable, pipeline?.models.find((m) => m.modelId === model.id)?.waiting ?? null);
  const jev = entry.kind === 'remote-jev';
  const planMax = entitlement?.limits.messagesPerDay ?? 0;
  const jevStatus = pipeline?.models.find((m) => m.modelId === model.id);
  const scoredToday = useScoredToday(jev && model.entitled) ?? jevStatus?.usedToday ?? usage?.scoredToday ?? null;
  const actions = actionsOfModel(rules, model.id);
  const order = models.map((m) => m.id);
  const index = order.indexOf(model.id);

  const setTrigger = (i: number, next: TriggerDraft): void => draft.set((d) => ({ ...d, triggers: d.triggers.map((t, j) => (j === i ? next : t)) }));
  const setOption = <K extends keyof ModelDraft['options']>(key: K, value: ModelDraft['options'][K]): void => draft.set((d) => ({ ...d, options: { ...d.options, [key]: value } }));

  const onSwitch = (checked: boolean): void => {
    if (checked && !model.entitled) {
      const error: DigesterErrorShape = { name: 'DigesterError', code: 'not_entitled', message: `${model.name} is part of Pro. On Free, the on-device model scores your messages.` };
      openDialog('upsell', { error });
      return;
    }
    draft.set((d) => ({ ...d, enabled: checked }));
  };

  const runTest = async (): Promise<void> => {
    setTesting(true);
    setTestError(null);
    try {
      setTest(await engine.models.test(model.id, runOf(v)));
    } catch (error) {
      const e = toDigesterError(error);
      if (e.code === 'invalid_input' && e.details?.issues?.length) setIssues(sortIssues(e.details.issues, v.groups.length));
      setTest(null);
      setTestError(e.message);
    } finally {
      setTesting(false);
    }
  };

  const move = async (delta: number): Promise<void> => {
    const target = index + delta;
    if (index < 0 || target < 0 || target >= order.length) return;
    const next: ModelId[] = [...order];
    [next[index], next[target]] = [next[target] as ModelId, next[index] as ModelId];
    try {
      await engine.models.reorder(next);
    } catch (error) {
      reportError(error);
    }
  };

  const confirmReset = (): void => {
    openDialog('confirm', {
      title: 'Reset ladder',
      message: 'Restore the six seeded rules — for Jev: Strong match (≥ 50 → Priority), Loose match (≥ 15 → Weak), Below the bar (→ Dismissed); for the on-device model: ≥ 45 → Priority, ≥ 20 → Weak, the rest → Dismissed?',
      note: 'They return with their default values. Your other scoring rules stay as they are.',
      confirmLabel: 'Reset ladder',
      onConfirm: async () => {
        try {
          const result = await engine.rules.resetLadder();
          showToast('Ladder reset', result.undoId);
        } catch (error) {
          reportError(error);
        }
      },
    });
  };

  const showLanguages = (): void => {
    openDialog('model-languages', { entry });
  };

  const folderName = (id: string): string => (id === 'any' ? 'any folder' : folders.find((f) => f.id === id)?.name ?? id);
  const fieldsRead = v.options.topicFields;
  const toggleField = (field: TopicField, on: boolean): void =>
    setOption('topicFields', on ? [...new Set([...fieldsRead, field])] : fieldsRead.filter((f) => f !== field));
  const paused = jev ? pipeline?.scoringPaused ?? null : null;
  const budget = v.options.dailyBudget === null ? planMax : Math.min(v.options.dailyBudget, planMax);

  return (
    <EditorFrame title={model.name} icon={model.icon} status={{ label: status.label, on: status.tone === 'on' }} enabled={{ checked: v.enabled && model.entitled, onChange: onSwitch }}>
      <SaveIslandTools>
        <Tool icon="play" label="Test run rule" onClick={() => void runTest()} />
      </SaveIslandTools>
      <div className="model-overview" data-model={model.id}>
        <div className="jev-head">
          <div className="model-recommendation">
            <Icon name={entry.recommendation ? 'sparkles' : 'info'} />
            {entry.recommendation || (jev ? 'Topic scoring' : 'On-device topic scoring')}
          </div>
          <span className="model-meters" aria-label="Accuracy and speed">
            <Meter label="accuracy" value={accuracyValue(entry)} />
            <Meter label="speed" value={speedValue(entry)} />
          </span>
        </div>
        <p className="model-description">{entry.description}</p>
        <div className="model-facts">
          <div className="model-fact" data-fact="accuracy">
            <span>
              <Icon name="target" />
              Topic ranking
            </span>
            <strong>{aucText(entry.quality.topicAuc)} AUC</strong>
            {entry.quality.topicAucNamesOnly !== null ? <small>{aucText(entry.quality.topicAucNamesOnly)} from names only</small> : null}
          </div>
          <div className="model-fact" data-fact="speed">
            <span>
              <Icon name="clock" />
              Speed
            </span>
            <strong>{speedText(entry)}</strong>
          </div>
          <div className="model-fact" data-fact="languages">
            <span>
              <Icon name="globe" />
              Languages
            </span>
            <button type="button" data-action="model-languages" onClick={showLanguages}>
              {entry.languages.label}
              <Icon name="chevron-right" />
            </button>
          </div>
          {onDevice ? (
            <>
              <div className="model-fact" data-fact="size">
                <span>
                  <Icon name="download" />
                  Download
                </span>
                <strong>{megabytes(onDevice.downloadBytes)}</strong>
              </div>
              <div className="model-fact" data-fact="memory">
                <span>
                  <Icon name="memory-stick" />
                  Memory
                </span>
                <strong>≈ {(onDevice.memoryMb / 1000).toFixed(1)} GB while it runs</strong>
              </div>
              <div className="model-fact" data-fact="licence">
                <span>
                  <Icon name="file-text" />
                  Licence
                </span>
                <strong>{onDevice.license}</strong>
              </div>
            </>
          ) : (
            <>
              <div className="model-fact" data-fact="where">
                <span>
                  <Icon name="server" />
                  Runs
                </span>
                <strong>Through Digester’s server</strong>
              </div>
              <div className="model-fact" data-fact="today">
                <span>
                  <Icon name="gauge" />
                  Today
                </span>
                <strong data-budget-today title={scoredToday === null ? 'Sign in to see the server count' : undefined}>
                  {scoredToday === null ? '—' : `${formatExact(scoredToday)} of ${formatExact(budget)}`}
                </strong>
              </div>
              <div className="model-fact" data-fact="plan">
                <span>
                  <Icon name="sparkles" />
                  Plan
                </span>
                <strong>{model.entitled ? 'Included in Pro' : 'Pro only'}</strong>
              </div>
            </>
          )}
        </div>
        <p className="model-guidance">Measured on {entry.quality.measuredOn}. The numbers are the catalogue’s measurement, not a benchmark of this device.</p>
        <ul className="model-caveats" aria-label="Known weaknesses">
          {entry.quality.caveats.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        {onDevice ? (
          <InstallBlock entry={onDevice} state={install} unavailable={unavailable} enabled={v.enabled} />
        ) : (
          <div className="model-install" data-model-install={model.id}>
            <div className="model-install-row">
              <span className="model-install-status">
                <Icon name={model.entitled && v.enabled ? 'check' : 'info'} />
                <span>{!model.entitled ? 'Jev is part of Pro' : v.enabled ? 'Scores the messages its run rule selects' : 'Switched off'}</span>
              </span>
              <div className="model-install-actions">
                {model.entitled ? null : (
                  <button type="button" className="primary-button" onClick={() => navigate('/subscribe')}>
                    <Icon name="sparkles" />
                    More with Pro
                  </button>
                )}
              </div>
            </div>
            {jevStatus && (jevStatus.pending > 0 || jevStatus.failed > 0) ? (
              <p className="model-preview-note" data-model-pending>
                Waiting for scores: {formatExact(jevStatus.pending)}
                {jevStatus.failed ? ` · failed: ${formatExact(jevStatus.failed)}` : ''}.
              </p>
            ) : null}
            {paused ? (
              <div className="editor-note scoring-paused-note" role="status" data-scoring-paused={paused.reason}>
                <Icon name="circle-alert" /> {scoringPauseText(paused)}
                {paused.reason === 'budget' ? ' Raise the daily budget under Options to continue today.' : ''}
              </div>
            ) : null}
            <p className="model-preview-note">Model {entry.version}. Your topics’ names and each selected message’s title and opening text are sent to the scorer through Digester’s server; nothing is stored there.</p>
          </div>
        )}
      </div>

      <Section title="Runs when" help="Any of these. A model scores a message by itself at most once.">
        <div className="model-triggers">
          {v.triggers.length === 0 ? <div className="editor-note">No triggers: this model never runs by itself.</div> : null}
          {v.triggers.map((t, i) => {
            const issue = issues?.triggers.get(i);
            return (
              <div className={issue ? 'model-trigger has-issue' : 'model-trigger'} key={i} data-trigger={i}>
                <Select<TriggerDraft['on']>
                  label="When"
                  name={`t${i}-on`}
                  value={t.on}
                  options={(Object.keys(TRIGGER_LABELS) as Array<TriggerDraft['on']>).map((k) => [k, TRIGGER_LABELS[k]] as const)}
                  onChange={(on) => setTrigger(i, { on, folderId: t.folderId })}
                />
                {t.on === 'request' ? (
                  <span className="action-note">Score now in the reader, Rescore, or your assistant</span>
                ) : (
                  <Select label="Folder" name={`t${i}-folder`} value={t.folderId} options={[['any', 'Any folder'] as const, ...folders.map((f) => [f.id, f.name] as const)]} onChange={(folderId) => setTrigger(i, { on: t.on, folderId })} />
                )}
                <Tool icon="x" label="Remove trigger" onClick={() => draft.set((d) => ({ ...d, triggers: d.triggers.filter((_, j) => j !== i) }))} />
                {issue ? (
                  <p className="field-issue" role="alert" data-issue={`trigger:${i}`}>
                    {issue}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
        {issues?.triggersAll.length ? (
          <p className="field-issue" role="alert" data-issue="triggers">
            {issues.triggersAll.join(' ')}
          </p>
        ) : null}
        <button type="button" className="add-line" onClick={() => draft.set((d) => ({ ...d, triggers: [...d.triggers, { on: 'automatic', folderId: 'inbox' }] }))}>
          <Icon name="plus" /> Add trigger
        </button>
        <p className="field-help">
          {v.triggers.length
            ? `Now: ${v.triggers.map((t) => (t.on === 'request' ? 'when you ask' : `${TRIGGER_LABELS[t.on].toLowerCase()} ${folderName(t.folderId)}`)).join('; ')}.`
            : 'Add a trigger to let it run.'}{' '}
          Moves by Digester itself (an archived folder, the plan) never trigger a model.
        </p>
      </Section>

      <Section title="Only if" help="Conditions on the message. No conditions = every message a trigger brings.">
        <ConditionGroups
          use="model"
          value={v}
          onChange={(next) => draft.set((d) => ({ ...d, ...next }))}
          emptyNote="No conditions: it scores every message its triggers bring."
          {...(issues ? { issues: issues.conditions } : {})}
        />
        {v.flattened ? <p className="field-help">This run rule was built with deeper nesting elsewhere. Saving here keeps its groups but flattens anything inside them.</p> : null}
        <div className="rule-test">
          <button className="secondary-button" type="button" disabled={testing} data-action="model-test" onClick={() => void runTest()}>
            <Icon name="play" /> Test
          </button>
          <span>What this rule would have scored in the last 7 days · nothing is scored</span>
        </div>
        <div id="rule-test-results" aria-live="polite">
          {testError ? <div className="test-summary is-error">{testError}</div> : null}
          {test ? (
            <>
              <div className="test-summary" data-model-test>
                Would have scored {formatExact(test.matched)} of {formatExact(test.evaluated)} messages of the last 7 days ≈ {formatExact(Math.round(test.perDay))} a day
                <span>nothing is scored by a test</span>
              </div>
              {test.rows.map(({ row, result }) => (
                <div className="test-row" key={row.id} data-result={result}>
                  <Icon name={result === 'match' ? 'check' : result === 'unknown' ? 'clock' : 'x'} />
                  <span>{row.title || row.excerpt}</span>
                  <b>{result === 'match' ? 'Would score' : result === 'unknown' ? 'Waits for content' : 'Skipped'}</b>
                </div>
              ))}
            </>
          ) : null}
        </div>
        {issues?.general.length ? (
          <p className="field-issue" role="alert" data-issue="general">
            {issues.general.join(' ')}
          </p>
        ) : null}
      </Section>

      <Section title="Actions on scoring" help="Scoring rules that test this model’s scores, in evaluation order. The first that matches acts.">
        <div className="model-actions-list">
          {actions.length ? (
            actions.map((r) => (
              <button type="button" className="model-action-link" key={r.id} data-rule={r.id} onClick={() => navigate(`/settings/models/${encodeURIComponent(r.id)}`)}>
                <Icon name={r.icon} />
                <span>
                  <strong>{r.name}</strong>
                  <small>{ruleSummary(r)}{r.enabled ? '' : ' · disabled'}</small>
                </span>
                <Icon name="chevron-right" />
              </button>
            ))
          ) : (
            <div className="editor-note">No scoring rule tests this model yet: its scores show as topics but move nothing.</div>
          )}
        </div>
        <div className="model-actions-tools">
          <button type="button" className="add-line" data-action="add-scoring-action" onClick={() => navigate(`/settings/models/new?model=${encodeURIComponent(model.id)}`)}>
            <Icon name="plus" /> Add action
          </button>
          <button type="button" className="secondary-button" data-action="reset-ladder" onClick={confirmReset}>
            <Icon name="rotate-ccw" /> Reset ladder
          </button>
        </div>
      </Section>

      <Section title="Options">
        <div className="model-topic-fields" data-topic-fields>
          <span className="model-option-label">Reads from a topic</span>
          {entry.topicFields.supported.length === 1 ? (
            <p className="model-note-row" data-reads="name-only">
              Reads the topic name only (up to {entry.topicFields.limits.nameChars} characters). Descriptions and paraphrases are not sent.
            </p>
          ) : (
            entry.topicFields.supported.map((field) => (
              <label className="model-check" key={field}>
                <input type="checkbox" name={`reads-${field}`} checked={field === 'name' || fieldsRead.includes(field)} disabled={field === 'name'} onChange={(e) => toggleField(field, e.target.checked)} />
                <span>
                  {FIELD_LABEL[field]}
                  <small>{fieldNote(entry, field)}</small>
                </span>
              </label>
            ))
          )}
          {issues?.options.get('topicFields') ? (
            <p className="field-issue" role="alert" data-issue="options.topicFields">
              {issues.options.get('topicFields')}
            </p>
          ) : null}
          {entry.topicFields.supported.length > 1 ? <p className="field-help">Changing what it reads rebuilds its topic vectors; messages already scored keep their scores until you rescore them.</p> : null}
        </div>
        {jev ? (
          <>
            <Select<BodyStep>
              label="Text sent per message"
              name="model-body-chars"
              disabled={!model.entitled}
              value={bodyStepOf(v.options.bodyMaxChars)}
              options={[
                ['500', 'Title + first 500 characters'],
                ['1000', 'Title + first 1,000 characters (default)'],
                ['3000', 'Title + first 3,000 characters'],
                ['all', 'Title + all text (up to the server’s cap)'],
              ]}
              onChange={(step) => setOption('bodyMaxChars', step === 'all' ? BODY_ALL : Number(step))}
            />
            <p className="field-help">The title and opening say what a message is about; the rest mostly adds cost. A message already scored is not scored again.</p>
            <Field
              label="Messages scored per day"
              name="model-daily-budget"
              type="number"
              inputMode="numeric"
              min={0}
              max={planMax || undefined}
              step={1}
              disabled={!model.entitled}
              value={v.options.dailyBudget === null ? String(planMax) : String(v.options.dailyBudget)}
              help={`0 to ${formatExact(planMax)} — your plan’s limit, which is the default. When today’s number is reached, Jev waits until 00:00 UTC; it applies on every device and to your assistant’s connection.`}
              onChange={(text) => {
                const n = text.trim() === '' ? planMax : Number(text);
                setOption('dailyBudget', Number.isFinite(n) && n === planMax ? null : Number.isFinite(n) ? Math.trunc(n) : v.options.dailyBudget);
              }}
            />
            {issues?.options.get('dailyBudget') ? <p className="field-issue" role="alert">{issues.options.get('dailyBudget')}</p> : null}
          </>
        ) : null}
        <Field
          label="Lowest score that shows a topic"
          name="model-topic-min"
          type="number"
          min={0}
          max={100}
          step={1}
          required
          value={v.options.topicMinScore}
          help="A topic scoring at least this appears on the message as a chip. It does not move anything; the actions on scoring do."
          onChange={(text) => setOption('topicMinScore', Number(text) as never)}
        />
        {issues?.options.get('topicMinScore') ? <p className="field-issue" role="alert">{issues.options.get('topicMinScore')}</p> : null}
      </Section>

      {index >= 0 ? (
        <Section title="Order">
          <div className="order-row" data-order={index + 1}>
            <strong>
              Position {index + 1} of {order.length}
            </strong>
            <button type="button" className="secondary-button" disabled={index === 0} onClick={() => void move(-1)}>
              <Icon name="arrow-up" /> Move up
            </button>
            <button type="button" className="secondary-button" disabled={index >= order.length - 1} onClick={() => void move(1)}>
              <Icon name="arrow-down" /> Move down
            </button>
          </div>
          <p className="field-help">When several models scored a message, the topics shown on it are those of the first one in this order. Models never wait for each other.</p>
        </Section>
      ) : null}
    </EditorFrame>
  );
}

function fieldNote(entry: ModelCatalogEntry, field: TopicField): string {
  const l = entry.topicFields.limits;
  if (field === 'name') return `Always read (first ${l.nameChars} characters); the fallback when the other fields are empty.`;
  if (field === 'phrases') return `Up to ${l.phrases ?? 0} short paraphrases of ${l.phraseChars ?? 0} characters each. Measured best (0.94 vs 0.87 from names only).`;
  return `The first ${formatExact(l.descriptionChars ?? 0)} characters, as one more text. Off by default: a long description measured worse than the bare name for this model.`;
}
